import { Component, Injector, OnInit, ViewChild } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { AutoAllocateDetail } from 'src/app/core/models/transport-operation/auto-allocate-detail';
import { ManualAllocationDetailSearchParams } from 'src/app/core/models/transport-operation/manual-allocation-detail-search-params';
import { MOHeaderSearchParams } from 'src/app/core/models/transport-operation/mo-header-search-params';
import { DataTableComponent } from 'src/app/shared/components/data-table/data-table.component';
import { SessionBaseComponent } from 'src/app/shared/components/session.base.component';
import { ManualAllocationService } from '../../services/manual-allocation.service';
import { UserPreferenceConstants } from 'src/app/core/constants/user-preference-constants.constant';
import { AutoAllocateRequest } from 'src/app/core/models/transport-operation/auto-allocation-request';
import { UrgentMODetail } from 'src/app/core/models/transport-operation/urgent-mo-detail';
import { UrgentMORequest } from 'src/app/core/models/transport-operation/urgent-mo-request';
import { PermissionConstants } from 'src/app/core/constants/permission-constants.constant';

@Component({
  selector: 'laas-manual-allocation',
  templateUrl: './manual-allocation.component.html',
  styleUrls: ['./manual-allocation.component.scss'],
})
export class ManualAllocationComponent extends SessionBaseComponent implements OnInit {
  @ViewChild('MOHeader') moHeader!: DataTableComponent;
  @ViewChild('MODetail') moDetail!: DataTableComponent;
  isSearchFormOpen: boolean = true;
  isHeaderLoading: boolean = false;
  isDetailLoading: boolean = false;
  submitted: boolean = false;
  readonly viewPermissionCode = PermissionConstants.ViewManualAllocation;
  readonly preferenceKeyMO: string = UserPreferenceConstants.ManualAllocationMO;
  readonly preferenceKeyMODetail: string = UserPreferenceConstants.ManualAllocationMODetail;
  currentLang: string = this.laasSetting.language;

  moHeaderColumns: any[];
  moDetailColumns: any[];
  searchForm: FormGroup;
  searchHeaderParams: MOHeaderSearchParams;
  autoAllocateRequest: AutoAllocateRequest;
  urgentMORequest: UrgentMORequest;

  headerResults: any[] = [];
  detailResults: any[] = [];

  constructor(
    injector: Injector,
    private formBuilder: FormBuilder,
    private manualAllocationService: ManualAllocationService
  ) {
    super(injector);

    this.searchForm = this.formBuilder.group({
      searchType: ['doNo'],
      externOrderKey: [null, Validators.required],
      mo: [{ value: null, disabled: true }],
      plant: [null, Validators.required]
    });

    this.searchForm.get('searchType')?.valueChanges.subscribe(type => {
      if (type === 'doNo') {
        this.searchForm.get('externOrderKey')?.enable();
        this.searchForm.get('externOrderKey')?.setValidators([Validators.required]);
        this.searchForm.get('mo')?.disable();
        this.searchForm.get('mo')?.clearValidators();
        this.searchForm.get('mo')?.setValue(null);
      } else {
        this.searchForm.get('mo')?.enable();
        this.searchForm.get('mo')?.setValidators([Validators.required]);
        this.searchForm.get('externOrderKey')?.disable();
        this.searchForm.get('externOrderKey')?.clearValidators();
        this.searchForm.get('externOrderKey')?.setValue(null);
      }
      this.searchForm.get('externOrderKey')?.updateValueAndValidity();
      this.searchForm.get('mo')?.updateValueAndValidity();
    });

    this.searchHeaderParams = {
      externOrderKey: null,
      mo: null,
      plant: null
    };

    this.autoAllocateRequest = {
      autoAllocateDetailList: [],
      userId: null
    };

    this.urgentMORequest = {
      urgentMODetailList: [],
      userId: null
    };

    this.moHeaderColumns = [
      { field: 'externOrderKey', header: 'ManualAllocation.HeaderColumn.DoNo', sort: false },
      { field: 'mo', header: 'ManualAllocation.HeaderColumn.Mo', sort: false },
      { field: 'deliveryId', header: 'ManualAllocation.HeaderColumn.DeliveryId', sort: false },
      { field: 'priority', header: 'ManualAllocation.HeaderColumn.Priority', sort: false },
      { field: 'moStatus', header: 'ManualAllocation.HeaderColumn.MoStatus', sort: false },
      { field: 'consolidationId', header: 'ManualAllocation.HeaderColumn.ConsolidationId', sort: false },
      { field: 'createUser', header: 'ManualAllocation.HeaderColumn.CreateUser', sort: false },
      { field: 'createDt', header: 'ManualAllocation.HeaderColumn.CreateDt', sort: false }
    ];

    this.moDetailColumns = [
      { field: 'orderLineNo', header: 'ManualAllocation.DetailColumn.OrderLineNo', sort: false },
      { field: 'pdNo', header: 'ManualAllocation.DetailColumn.PdNo', sort: false },
      { field: 'sku', header: 'ManualAllocation.DetailColumn.SKU', sort: false },
      { field: 'storer', header: 'ManualAllocation.DetailColumn.Storer', sort: false },
      { field: 'moItemStatus', header: 'ManualAllocation.DetailColumn.MoItemStatus', sort: false },
      { field: 'originalQty', header: 'ManualAllocation.DetailColumn.OriginalQty', sort: false },
      { field: 'openQty', header: 'ManualAllocation.DetailColumn.OpenQty', sort: false },
      { field: 'shippedQty', header: 'ManualAllocation.DetailColumn.ShippedQty', sort: false },
      { field: 'adjustedQty', header: 'ManualAllocation.DetailColumn.AdjustedQty', sort: false },
      { field: 'qtyAllocated', header: 'ManualAllocation.DetailColumn.QtyAllocated', sort: false },
      { field: 'brand', header: 'ManualAllocation.DetailColumn.Brand', sort: false },
      { field: 'specLocType', header: 'ManualAllocation.DetailColumn.SpecLocType', sort: false },
      { field: 'vendorDc', header: 'ManualAllocation.DetailColumn.VendorDc', sort: false },
      { field: 'vendorLot', header: 'ManualAllocation.DetailColumn.VendorLot', sort: false },
      { field: 'createUser', header: 'ManualAllocation.DetailColumn.CreateUser', sort: false },
      { field: 'createDt', header: 'ManualAllocation.DetailColumn.CreateDt', sort: false },
    ];
  }

  async ngOnInit(): Promise<void> {
    await super.ngOnInit();
  }

  private normalizeNullableString(value: string | null | undefined): string | null {
    const normalizedValue = value?.trim();
    return normalizedValue ? normalizedValue : null;
  }

  private getSelectedExternOrderKey(rowData: any): string | null {
    return rowData?.externOrderKey ?? rowData?.doNo ?? rowData?.pdNo ?? null;
  }

  searchBtnClick() {
    this.isHeaderLoading = true;
    if (this.searchForm.invalid) {
      this.submitted = true;
      const warningMessage = this.translateService.instant('ReceiptFormQuery.Alert.CheckRequired');
      this.toastService.warn(warningMessage);
      this.isHeaderLoading = false;
      return;
    }

    this.isSearchFormOpen = false;
    this.searchHeaderParams = {
      externOrderKey: this.normalizeNullableString(this.searchForm.get('externOrderKey')?.value),
      mo: this.normalizeNullableString(this.searchForm.get('mo')?.value),
      plant: this.searchForm.get('plant')?.value
    };

    this.manualAllocationService.searchMOHeader(this.searchHeaderParams).subscribe({
      next: (results: any[]) => {
        this.headerResults = results;
        this.detailResults = [];
        if (this.moHeader) {
          this.moHeader.selectedRows = [];
        }
        if (results.length !== 0) {
          const defaultHeader = results[0];
          if (this.moHeader) {
            this.moHeader.selectedRows = [defaultHeader];
          }
          this.searchDetail({
            externOrderKey: this.getSelectedExternOrderKey(defaultHeader),
            plant: this.searchHeaderParams.plant
          });
        }
      },
      error: (err: any) => {
        this.headerResults = [];
        this.detailResults = [];
        this.toastErrorMessage(err);
      },
      complete: () => {
        this.isHeaderLoading = false;
      },
    });
  }

  searchDetail(searchParams: ManualAllocationDetailSearchParams) {
    this.isDetailLoading = true;
    this.manualAllocationService.searchMODetail(searchParams).subscribe({
      next: (results: any[]) => {
        this.detailResults = results;
      },
      error: (err: any) => {
        this.detailResults = [];
        this.toastErrorMessage(err);
      },
      complete: () => {
        this.isDetailLoading = false;
      },
    });
  }

  onHeaderRowSelect(rowData: any) {
    const externOrderKey = this.getSelectedExternOrderKey(rowData);
    if (!externOrderKey) {
      this.detailResults = [];
      return;
    }

    this.searchDetail({
      externOrderKey,
      plant: this.searchHeaderParams.plant ?? this.searchForm.controls.plant.value
    });
  }

  allocateMO(event: any, rowdata: any) {
    this.routeStateService.navigateTo(
      '/TransportOperation/ManualAllocationWMS',
      {
        moDetail: {
          ...rowdata,
          externOrderKey: this.getSelectedExternOrderKey(rowdata),
          pdNo: this.getSelectedExternOrderKey(rowdata)
        }
      },
      true
    );
  }

  resetBtnClick() {
    this.searchForm.reset({
      searchType: 'doNo',
      externOrderKey: null,
      mo: null,
      plant: null
    });
    this.headerResults = [];
    this.detailResults = [];
    if (this.moHeader) {
      this.moHeader.selectedRows = [];
    }
    this.submitted = false;
    this.isSearchFormOpen = true;
  }


  autoAllocationBtnClick(event: any) {
    this.autoAllocateRequest.autoAllocateDetailList = (this.moHeader.selectedRows ?? []).map((row: any): AutoAllocateDetail => ({
      pdNo: this.getSelectedExternOrderKey(row)
    }));
    this.autoAllocateRequest.userId = this.userService.userInfo.userName;

    this.manualAllocationService.doWMSAutoAllocation(this.autoAllocateRequest)
      .subscribe({
        next: (result) => {
          const isSuccess = result.flag;
          if (!isSuccess) {
            this.toastErrorMessage(result.message);
            this.searchBtnClick();
            return;
          }
          this.toastService.success(this.translateService.instant('ManualAllocation.WMS.AllocationSuccess'));
          this.searchBtnClick();
        },
        error: (err) => {
          this.toastErrorMessage(err.error);
        },
        complete: () => {
          this.isSearchFormOpen = false;
        }
      });
  }

  urgentBtnClick(event: any) {

    this.urgentMORequest.urgentMODetailList = (this.moHeader.selectedRows ?? []).map((row: any): UrgentMODetail => {
      const externOrderKey = this.getSelectedExternOrderKey(row);
      return {
        externOrderKey,
        pdNo: externOrderKey
      };
    });
    this.urgentMORequest.userId = this.userService.userInfo.userName;

    console.log("urgentMORequest:" + JSON.stringify(this.urgentMORequest));

    this.manualAllocationService.doWMSUrgentMO(this.urgentMORequest)
      .subscribe({
        next: (result) => {
          const isSuccess = result.flag;
          if (!isSuccess) {
            this.toastErrorMessage(result.message);
            this.searchBtnClick();
            return;
          }
          this.toastService.success(this.translateService.instant('ManualAllocation.WMS.UrgentMOSuccess'));
          this.searchBtnClick();
        },
        error: (err) => {
          this.toastErrorMessage(err.error);
        },
        complete: () => {
          this.isSearchFormOpen = false;
        }
      });
  }

}
